/**
 * What can be done to a schedule, from its row and from its detail header alike.
 *
 * Three actions: **run now**, **edit** and **delete**. Each needs
 * `schedule.manage`, and a job may ask for one more permission on top
 * (`notify.send` asks for `notification.manage`). The server checks both; an
 * action whose job needs a permission the caller lacks is shown disabled with
 * that reason, rather than failing with a 403 after a click.
 *
 * The form is exported, so `SchedulesPage`'s create dialog renders the same
 * fields. It shows the next run times as you type — the server answers them,
 * so the preview and the runner read an expression the same way.
 *
 * Editing **sends only what changed**: the server audits both sides of every
 * field it is given.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import {
  SCHEDULE_LIMITS,
  type CreateScheduleRequest,
  type ScheduleDto,
  type ScheduleJobDto,
  type UpdateScheduleRequest,
} from '@forsys/shared';
import { deleteSchedule, previewSchedule, runSchedule, updateSchedule } from '../../api/schedules';
import { useAuth } from '../../auth/useAuth';
import { useAsync } from '../../hooks/useAsync';
import { useDebounced } from '../../hooks/useDebounced';
import {
  formAction,
  useEntityActions,
  type EntityActionSpec,
  type EntityActionsState,
} from '../../hooks/useEntityActions';

/** The fields a schedule has, as the dialog holds them. The input is JSON text until it is sent. */
export interface ScheduleForm {
  name: string;
  job: string;
  inputText: string;
  cron: string;
  timeZone: string;
  isEnabled: boolean;
}

/** The zone this browser is in — the default for a new schedule, since that is the clock its author reads. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Every zone this browser knows, for the picker. Empty where the API is missing; the field still takes text. */
function knownTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  return intl.supportedValuesOf?.('timeZone') ?? [];
}

function inputTextFor(value: unknown): string {
  return value === null || value === undefined ? '' : JSON.stringify(value, null, 2);
}

export function blankScheduleForm(): ScheduleForm {
  return { name: '', job: '', inputText: '', cron: '', timeZone: browserTimeZone(), isEnabled: true };
}

export function scheduleFormFor(schedule: ScheduleDto): ScheduleForm {
  return {
    name: schedule.name,
    job: schedule.job,
    inputText: inputTextFor(schedule.input),
    cron: schedule.cron,
    timeZone: schedule.timeZone,
    isEnabled: schedule.isEnabled,
  };
}

/** The input text as a value, or `undefined` when it is not JSON. Empty text is no input. */
function parseInputText(text: string): { value: unknown } | undefined {
  if (text.trim() === '') return { value: null };
  try {
    return { value: JSON.parse(text) as unknown };
  } catch {
    return undefined;
  }
}

export function scheduleIncomplete(form: ScheduleForm): boolean {
  return (
    form.name.trim() === '' ||
    form.job === '' ||
    form.cron.trim() === '' ||
    form.timeZone.trim() === '' ||
    parseInputText(form.inputText) === undefined
  );
}

export function createRequestFrom(form: ScheduleForm): CreateScheduleRequest {
  const input = parseInputText(form.inputText)?.value ?? null;
  return {
    name: form.name.trim(),
    job: form.job,
    cron: form.cron.trim(),
    timeZone: form.timeZone.trim(),
    isEnabled: form.isEnabled,
    ...(input !== null ? { input } : {}),
  };
}

/** What the form changed, and nothing else. */
function changesBetween(schedule: ScheduleDto, form: ScheduleForm): UpdateScheduleRequest {
  const changes: UpdateScheduleRequest = {};
  if (form.name.trim() !== schedule.name) changes.name = form.name.trim();
  if (form.job !== schedule.job) changes.job = form.job;
  if (form.cron.trim() !== schedule.cron) changes.cron = form.cron.trim();
  if (form.timeZone.trim() !== schedule.timeZone) changes.timeZone = form.timeZone.trim();
  if (form.isEnabled !== schedule.isEnabled) changes.isEnabled = form.isEnabled;
  const input = parseInputText(form.inputText)?.value ?? null;
  // Compared as JSON, so re-indenting the text is not a change.
  if (JSON.stringify(input) !== JSON.stringify(schedule.input ?? null) || changes.job !== undefined) {
    changes.input = input;
  }
  return changes;
}

/** A time the preview lists, in the schedule's own zone — the zone its expression is read in. */
function formatRun(iso: string, timeZone: string, language: string): string {
  // The weekday shows, because "every hour on Friday" is what people check.
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  };
  try {
    return new Intl.DateTimeFormat(language, { ...options, timeZone }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat(language, options).format(new Date(iso));
  }
}

export interface ScheduleFieldsProps {
  form: ScheduleForm;
  patch: (changes: Partial<ScheduleForm>) => void;
  jobs: readonly ScheduleJobDto[];
}

/** The inputs, shared by the create dialog and the edit action. */
export function ScheduleFields({ form, patch, jobs }: ScheduleFieldsProps) {
  const { t, i18n } = useTranslation('schedules');
  const zones = useMemo(knownTimeZones, []);

  // The server reads the expression, so the preview is exactly what the runner
  // will do. Debounced: one request per pause, not per keystroke.
  const timing = useDebounced(JSON.stringify([form.cron.trim(), form.timeZone.trim()]), 400);
  const [cron, timeZone] = JSON.parse(timing) as [string, string];
  const preview = useAsync(() => previewSchedule(cron, timeZone), [timing], {
    enabled: cron !== '' && timeZone !== '',
  });

  const job = jobs.find((one) => one.key === form.job);
  const inputValid = parseInputText(form.inputText) !== undefined;

  const cronHelp =
    cron === ''
      ? t('dialog.cronHelp')
      : preview.error
        ? preview.error.message
        : preview.data
          ? t('dialog.next', {
              times: preview.data.runs.map((run) => formatRun(run, preview.data!.timeZone, i18n.language)).join(' · '),
            })
          : t('dialog.checking');

  return (
    <>
      <TextField
        label={t('dialog.name')}
        value={form.name}
        onChange={(event) => patch({ name: event.target.value })}
        slotProps={{ htmlInput: { maxLength: SCHEDULE_LIMITS.name } }}
        autoFocus
        fullWidth
      />

      <TextField
        select
        label={t('dialog.job')}
        value={form.job}
        onChange={(event) => {
          const next = jobs.find((one) => one.key === event.target.value);
          // The old input rarely fits another job; start from the new one's example.
          patch({ job: event.target.value, inputText: inputTextFor(next?.exampleInput) });
        }}
        helperText={
          job?.permission ? t('dialog.jobPermission', { permission: job.permission }) : t('dialog.jobHelp')
        }
        fullWidth
      >
        {jobs.map((one) => (
          <MenuItem key={one.key} value={one.key}>
            <ListItemText primary={one.key} secondary={one.description} />
          </MenuItem>
        ))}
      </TextField>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label={t('dialog.cron')}
          value={form.cron}
          onChange={(event) => patch({ cron: event.target.value })}
          placeholder="0 * * * 5"
          error={cron !== '' && preview.error !== undefined}
          helperText={cronHelp}
          slotProps={{ htmlInput: { maxLength: SCHEDULE_LIMITS.cron, style: { fontFamily: 'monospace' } } }}
          sx={{ flex: 3 }}
        />
        <Autocomplete
          freeSolo
          options={zones}
          value={form.timeZone}
          onInputChange={(_event, value) => patch({ timeZone: value })}
          sx={{ flex: 2 }}
          renderInput={(params) => <TextField {...params} label={t('dialog.timeZone')} />}
        />
      </Stack>

      <TextField
        label={t('dialog.input')}
        value={form.inputText}
        onChange={(event) => patch({ inputText: event.target.value })}
        error={!inputValid}
        helperText={!inputValid ? t('dialog.inputInvalid') : (job?.description ?? t('dialog.inputHelp'))}
        multiline
        minRows={4}
        slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: 13 } } }}
        fullWidth
      />

      <FormControlLabel
        control={<Switch checked={form.isEnabled} onChange={(event) => patch({ isEnabled: event.target.checked })} />}
        label={t('dialog.enabled')}
      />
    </>
  );
}

export interface UseScheduleActionsOptions {
  /** The job catalogue, to know which permission each schedule's job needs. */
  jobs: readonly ScheduleJobDto[];
  /** After an edit or a run. Reload the list, or the record and its runs. */
  onChanged: () => void;
  /** After a delete. Reload the list, or leave the detail screen. */
  onDeleted: () => void;
}

export function useScheduleActions(options: UseScheduleActionsOptions): EntityActionsState<ScheduleDto> {
  const { t } = useTranslation('schedules');
  const { can } = useAuth();
  const { jobs } = options;

  /** Why the caller cannot act on this schedule beyond `schedule.manage`, or null. */
  const jobLock = (schedule: ScheduleDto): string | null => {
    const permission = jobs.find((one) => one.key === schedule.job)?.permission;
    return permission && !can(permission) ? t('actions.needsPermission', { permission }) : null;
  };

  const specs: EntityActionSpec<ScheduleDto>[] = [
    {
      kind: 'immediate',
      key: 'run',
      label: t('actions.run'),
      icon: <PlayArrowOutlinedIcon fontSize="small" />,
      permission: 'schedule.manage',
      primary: true,
      lockedReason: jobLock,
      run: (schedule) => runSchedule(schedule.id),
    },
    formAction<ScheduleDto, ScheduleForm>({
      kind: 'form',
      key: 'edit',
      label: t('actions.edit'),
      icon: <EditOutlinedIcon fontSize="small" />,
      permission: 'schedule.manage',
      lockedReason: jobLock,
      blank: blankScheduleForm(),
      formFor: scheduleFormFor,
      fields: (form, patch) => <ScheduleFields form={form} patch={patch} jobs={jobs} />,
      incomplete: scheduleIncomplete,
      title: (schedule) => t('dialog.editTitle', { name: schedule.name }),
      submitLabel: t('dialog.save'),
      maxWidth: 'sm',
      run: async (schedule, form) => {
        const changes = changesBetween(schedule, form);
        if (Object.keys(changes).length === 0) return { schedule };
        return updateSchedule(schedule.id, changes);
      },
    }),
    {
      kind: 'confirm',
      key: 'delete',
      label: t('actions.delete'),
      icon: <DeleteOutlinedIcon fontSize="small" />,
      permission: 'schedule.manage',
      tone: 'error',
      lockedReason: jobLock,
      title: (schedule) => t('deleteDialog.title', { name: schedule.name }),
      message: t('deleteDialog.message'),
      confirmLabel: t('deleteDialog.confirm'),
      run: (schedule) => deleteSchedule(schedule.id),
    },
  ];

  return useEntityActions<ScheduleDto>(specs, {
    onChanged: (_result, key) => (key === 'delete' ? options.onDeleted() : options.onChanged()),
  });
}
